<template>
  <div class="form-box-wrapper">
    <div
      :id="getId()"
      ref="surveyFormContainer"
      v-focus-trap
      class="form-box tab-focused-btn"
      tabindex="0"
      role="form"
      aria-label="survey form"
      :class="{
        'form-box-compact': isMobileScreenOrCompactWidget,
        'form-box-compact-fullscreen':
          isMobileScreenOrCompactWidget && isValidSentinent(currSentinent)
      }"
    >
      <div
        v-if="isSurveySubmitted"
        class="close-icon-container"
        :class="{
          'close-icon-container-compact': isMobileScreenOrCompactWidget
        }"
      >
        <button class="close-icon-btn" @click="handleSurveySkip">
          <BaseIcon name="CloseIcon" fill="#6D6D6D" />
        </button>
      </div>
      <div
        v-if="
          isValidSentinent(currSentinent) &&
          (schema.reasonOfRating.required ||
            schema.resolutionQuestion.required ||
            schema.additionalFeedback.required)
        "
        class="mandatory-text-container"
      >
        <p
          :style="{ ...getFontSizeForChatInterface('bodyText') }"
          class="required-field mandatory-text"
        >
          * Mandatory
        </p>
      </div>
      <div
        class="form-box-content"
        :class="{
          'form-box-content-compact': isMobileScreenOrCompactWidget,
          'form-box-content-opened': isValidSentinent(currSentinent)
        }"
      >
        <div
          v-if="schema.feedbackMessage.enabled"
          class="survey-header-txt"
          :role="isFirstResponse ? 'alert' : null"
          :style="{ ...getFontSizeForChatInterface('title') }"
        >
          {{
            sendTranslatedValue(
              `${surveyForm.SURVEY_DOT}${surveyId}${surveyForm.DOT}${surveyForm.BASIC_INFO}`,
              schema.feedbackMessage.text
            )
          }}
        </div>
        <div
          class="rating-box"
          :class="{
            'rating-box-compact': isMobileScreenOrCompactWidget,
            'rating-box-readOnly': isReadOnly
          }"
        >
          <component
            :is="getRatingComponent"
            :disabled="isReadOnly"
            :selected="isValidSentinent(currSentinent)"
            :rating-changed="onRatingChangeHandler"
            :schema="schema"
            :numeric-rating="numericRating"
          />
        </div>
        <div
          v-if="isValidSentinent(currSentinent)"
          class="survey-form-content"
          :class="{
            'survey-form-content-compact': isMobileScreenOrCompactWidget
          }"
        >
          <div
            v-if="schema.resolutionQuestion.enabled"
            class="resolution"
            :class="{ 'resolution-compact': isMobileScreenOrCompactWidget }"
          >
            <div class="res-questn">
              <span
                v-if="schema.resolutionQuestion.required"
                aria-hidden="true"
                class="required-field"
                >* </span
              ><span :style="{ ...getFontSizeForChatInterface('bodyText') }">{{
                sendTranslatedValue(
                  `${surveyForm.SURVEY_DOT}${surveyId}${surveyForm.DOT}${surveyForm.ISSUE_RESOLUTION_FEEDBACK}`,
                  schema.resolutionQuestion.text
                )
              }}</span>
              <span v-if="schema.resolutionQuestion.required" class="sr-only">
                (required)
              </span>
            </div>
            <div class="resolution-buttons">
              <button
                ref="yes-res-btn"
                class="resolution-button custom-btn tab-focused-btn"
                :class="{ 'custom-btn-readOnly': isSurveySubmitted }"
                :style="{ ...getFontSizeForChatInterface('bodyText') }"
                aria-pressed="false"
                @click="onYesResolutionButtonClicked"
              >
                {{
                  sendTranslatedValue(
                    `${surveyForm.BUTTON_YES}`,
                    `${surveyForm.YES}`
                  )
                }}
              </button>
              <button
                ref="no-res-btn"
                class="resolution-button custom-btn tab-focused-btn"
                :class="{ 'custom-btn-readOnly': isSurveySubmitted }"
                :style="{ ...getFontSizeForChatInterface('bodyText') }"
                aria-pressed="false"
                @click="onNoResolutionButtonClicked"
              >
                {{
                  sendTranslatedValue(
                    `${surveyForm.BUTTON_NO}`,
                    `${surveyForm.NO}`
                  )
                }}
              </button>
            </div>
          </div>
          <div
            v-if="schema.reasonOfRating.enabled"
            class="suggestion-container"
          >
            <div class="suggestion-header-text">
              <span
                v-if="schema.reasonOfRating.required"
                aria-hidden="true"
                class="required-field"
                >* </span
              ><span :style="{ ...getFontSizeForChatInterface('bodyText') }">{{
                getCurrentSuggestionTitle
              }}</span>
              <span v-if="schema.reasonOfRating.required" class="sr-only">
                (required)
              </span>
            </div>
            <div
              class="suggestion-options"
              :class="{
                'suggestion-options-compact': isMobileScreenOrCompactWidget
              }"
            >
              <button
                v-for="(text, index) in getCurrentSuggestionMap"
                :key="text"
                :class="{ 'custom-btn-readOnly': isSurveySubmitted }"
                class="tab-focused-btn custom-btn suggestions"
                aria-pressed="false"
                :style="{ ...getFontSizeForChatInterface('bodyText') }"
                @click="suggestionClick"
              >
                {{ getTranslationValue(index, text) }}
              </button>
            </div>
          </div>
          <div
            v-if="schema.additionalFeedback.enabled && !feedbackInputEnabled"
            class="additional-feedback"
            :class="{
              'additional-feedback-compact': isMobileScreenOrCompactWidget
            }"
          >
            <div>
              <span
                v-if="schema.additionalFeedback.required"
                aria-hidden="true"
                class="required-field"
                >* </span
              ><span :style="{ ...getFontSizeForChatInterface('bodyText') }">{{
                sendTranslatedValue(
                  `${surveyForm.ADDITIONAL_FEEDBACK}`,
                  `${surveyForm.ADDITIONAL_FEEDBACK_CONTEXT}`
                )
              }}</span>
              <span v-if="schema.additionalFeedback.required" class="sr-only">
                (required)
              </span>
            </div>
            <button
              class="tab-focused-btn additional-feedback-btn-container"
              :aria-label="
                sendTranslatedValue(
                  `${surveyForm.ADDITIONAL_FEEDBACK}`,
                  `${surveyForm.ADDITIONAL_FEEDBACK_CONTEXT}`
                )
              "
              @click="onAdditionalFeedbackAdd"
            >
              <BaseIcon name="AddIcon" />
            </button>
          </div>
          <div
            v-if="schema.additionalFeedback.enabled && feedbackInputEnabled"
            class="additional-feedback-input"
            :class="{
              'additional-feedback-input-compact': isMobileScreenOrCompactWidget
            }"
            :style="{ ...getFontSizeForChatInterface('bodyText') }"
          >
            <div>
              <span
                v-if="schema.additionalFeedback.required"
                aria-hidden="true"
                class="required-field"
                >* </span
              ><span :style="{ ...getFontSizeForChatInterface('bodyText') }">{{
                sendTranslatedValue(
                  `${surveyForm.SURVEY_DOT}${surveyId}${surveyForm.DOT}${surveyForm.ADDITIONAL_FEEDBACK_TEXT}`,
                  schema.additionalFeedback.text
                )
              }}</span>
              <span v-if="schema.additionalFeedback.required" class="sr-only">
                (required)
              </span>
            </div>
            <textarea
              ref="additional-feedback-textarea"
              v-model="feebackText"
              class="feedback-textarea"
              :class="{ 'feedback-textarea-disabled': isSurveySubmitted }"
              :placeholder="
                sendTranslatedValue(
                  `${surveyForm.START_TYPING}`,
                  'Start Typing'
                )
              "
              maxlength="200"
              :disabled="isSurveySubmitted"
            />
            <div
              class="char-feedback-count"
              :style="{ ...getFontSizeForChatInterface('bodyText') }"
            >
              {{ getCharacterCountInFeedback }}
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="
          schema.isSkipEnabled ||
          isReadOnly ||
          (isValidSentinent(currSentinent) && !isReadOnly)
        "
        class="buttons-outer-container"
        :class="{
          'buttons-outer-container-compact': isMobileScreenOrCompactWidget,
          'buttons-outer-container-compact-fullscreen':
            isMobileScreenOrCompactWidget && isValidSentinent(currSentinent)
        }"
      >
        <button
          v-if="schema.isSkipEnabled || isReadOnly"
          class="skip-button tab-focused-btn"
          :style="{ ...getFontSizeForChatInterface('bodyText') }"
          @click="handleSurveySkip"
        >
          {{
            sendTranslatedValue(
              `${!isReadOnly ? surveyForm.SKIP : surveyForm.CLOSE}`,
              convertToTitleCase(
                `${!isReadOnly ? surveyForm.SKIP : surveyForm.CLOSE}`
              )
            )
          }}
        </button>
        <button
          v-if="isValidSentinent(currSentinent) && !isReadOnly"
          :disabled="!isSubmitButtonValid"
          class="submit-button tab-focused-btn"
          :style="{ ...getFontSizeForChatInterface('bodyText') }"
          @click="handleSurveySubmit"
        >
          {{
            sendTranslatedValue(
              `${surveyForm.SUBMIT}`,
              convertToTitleCase(`${surveyForm.SUBMIT}`)
            )
          }}
        </button>
      </div>
    </div>
  </div>
</template>
<script>
import { surveyForm, feedback } from '@/services/constants'
import BaseIcon from '@/views/BaseIcon.vue'
import StarRating from './RatingType/StarRating.vue'
import EmojiRating from './RatingType/EmojiRating.vue'
import NumberRating from './RatingType/NumberRating.vue'
import ThumbsRating from './RatingType/ThumbsRating.vue'
import { RATING } from '../constant'
import { focusOnChatInputField, getDeviceType } from '@/services/utils'
import fontSizeMixin from '@/mixins/fontSizeMixin'

export default {
  name: 'SurveyForm',
  components: {
    BaseIcon,
    StarRating,
    EmojiRating,
    NumberRating,
    ThumbsRating
  },
  mixins: [fontSizeMixin],
  props: {
    schema: {
      type: Object,
      default: () => {}
    },
    surveyId: {
      type: String,
      default: ''
    },
    isFirstResponse: {
      type: Boolean,
      default: false
    },
    triggerType: {
      type: String,
      default: ''
    },
    isSurveySubmitted: {
      type: Boolean,
      default: false
    },
    submittedValues: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      isReadOnly: false,
      surveyForm: surveyForm,
      currSuggestionKey: '',
      currSentinent: Infinity,
      feedbackInputEnabled: false,
      feebackText: '',
      numericRating: 5,
      submitSurveyInfo: {
        rating: '',
        suggestions: [],
        suggestionTitle: '',
        issueResolved: null,
        additionalFeedback: '',
        feedbackValue: '' // POSITIVE/NEGATIVE
      }
    }
  },
  computed: {
    isSubmitButtonValid() {
      let result = true
      if (
        this.schema.reasonOfRating?.enabled &&
        this.schema.reasonOfRating?.required
      ) {
        result =
          result &&
          this.submitSurveyInfo.suggestions &&
          this.submitSurveyInfo.suggestions.length > 0
      }
      if (
        this.schema.resolutionQuestion?.enabled &&
        this.schema.resolutionQuestion?.required
      ) {
        result =
          (result && this.submitSurveyInfo.issueResolved === true) ||
          this.submitSurveyInfo.issueResolved === false
      }
      if (
        this.schema.additionalFeedback?.enabled &&
        this.schema.additionalFeedback?.required
      ) {
        result = result && this.feebackText.length > 0
      }
      return result
    },
    isMobileScreenOrCompactWidget() {
      return (
        getDeviceType() === 'mobile' ||
        !this.$store.state.isChatWidgetFullScreen
      )
    },
    getCurrentSuggestionTitle() {
      const key =
        this.currSentinent < this.schema.surveyRatingTypeEnabledInfo.criteria
          ? surveyForm.NEGATIVE_MAP_KEY
          : surveyForm.POSITIVE_MAP_KEY
      const translationKey = surveyForm.POSITIVE_MAP_KEY
        ? `${surveyForm.SURVEY_DOT}${this.surveyId}${surveyForm.DOT}${surveyForm.POSITIVE_FEEDBACK_HEADER}`
        : `${surveyForm.SURVEY_DOT}${this.surveyId}${surveyForm.DOT}${surveyForm.NEGATIVE_FEEDBACK_HEADER}`
      this.submitSurveyInfo.suggestionTitle = this.schema[key].title
      return this.sendTranslatedValue(translationKey, this.schema[key].title)
    },
    getCurrentSuggestionMap() {
      if (
        this.currSentinent < this.schema.surveyRatingTypeEnabledInfo.criteria
      ) {
        this.submitSurveyInfo.feedbackValue = surveyForm.NEGATIVE
        this.currSuggestionKey = surveyForm.NEGATIVE
        return this.schema.negativeSuggestionMap.options
      }
      this.currSuggestionKey = surveyForm.POSITIVE
      this.submitSurveyInfo.feedbackValue = surveyForm.POSITIVE
      return this.schema.positiveSuggestionMap.options
    },
    getRatingComponent() {
      let ratingComponent = ''
      switch (this.schema.ratingTypeEnabled) {
        case RATING.STAR.value:
          ratingComponent = RATING.STAR.component
          break
        case RATING.EMOJI.value:
          ratingComponent = RATING.EMOJI.component
          break
        case RATING.NUM_5.value:
          ratingComponent = RATING.NUM_5.component
          this.numericRating = 5
          break
        case RATING.NUM_10.value:
          ratingComponent = RATING.NUM_10.component
          this.numericRating = 10
          break
        case RATING.THUMBS_UP_DOWN.value:
          ratingComponent = RATING.THUMBS_UP_DOWN.component
          break
        default:
          break
      }
      return ratingComponent
    },
    getCharacterCountInFeedback() {
      return `${this.feebackText.length}/200`
    }
  },
  watch: {
    schema() {
      this.currSentinent = Infinity
      this.feedbackInputEnabled = false
      this.feebackText = ''
    },
    'submitSurveyInfo.feedbackValue': function () {
      this.submitSurveyInfo.suggestions = []
    }
  },
  mounted() {
    this.feedbackInputEnabled =
      this.schema?.additionalFeedback?.showInputField || false
    if (this.isSurveySubmitted) {
      this.populateSurveyValues()
    }
    if (this.schema.isPersistentSurveyEnabled) {
      this.handlePersistentSurvey()
    }
    if (this.$store.state.isVoiceModeActive) {
      this.$store.state.shouldRestartVoiceRecording = false
    }
    // Ensure focus is set to the form container for accessibility
    // Use a longer delay for Safari 26 to ensure proper focus after all DOM operations
    setTimeout(() => {
      if (this.$refs.surveyFormContainer) {
        this.$refs.surveyFormContainer.focus()
      }
    }, 100)
  },
  methods: {
    populateSurveyValues() {
      let childNodes = document.querySelector('.sentinel-box')?.childNodes
      childNodes = Array.from(childNodes).filter(
        ({ tagName }) => tagName?.toLowerCase() === 'button'
      )
      childNodes[this.submittedValues.rating - 1]?.click()
      this.$nextTick(function () {
        this.submittedValues.issueResolved
          ? this.$refs['yes-res-btn']?.click()
          : this.$refs['no-res-btn']?.click()
        document
          .querySelector('.suggestion-options')
          ?.childNodes?.forEach((childNode) => {
            if (
              this.submittedValues.suggestions.includes(childNode.innerText)
            ) {
              childNode?.click()
            }
          })
        this.onAdditionalFeedbackAdd()
        this.feebackText = this.submittedValues.additionalFeedback
        this.isReadOnly = true
      })
    },
    getId() {
      return `survey-form-container-${this.triggerType}`
    },
    isValidSentinent(currSentinent) {
      return typeof currSentinent === 'number' && isFinite(currSentinent)
    },
    getTranslationValue(index, text) {
      if (this.currSuggestionKey === surveyForm.NEGATIVE) {
        return this.sendTranslatedValue(
          `${surveyForm.SURVEY_DOT}${this.surveyId}${surveyForm.DOT}${surveyForm.NEGATIVE_CHOICES_CHOICE_}${index + 1}`,
          text
        )
      } else {
        return this.sendTranslatedValue(
          `${surveyForm.SURVEY_DOT}${this.surveyId}${surveyForm.DOT}${surveyForm.POSITIVE_CHOICES_CHOICE_}${index + 1}`,
          text
        )
      }
    },
    sendTranslatedValue(key, defaultValue) {
      return this.$te(key) ? this.$t(key) : defaultValue
    },
    convertToTitleCase(inputString) {
      return inputString.charAt(0) + inputString.slice(1).toLowerCase()
    },
    suggestionClick(e) {
      if (this.isReadOnly) return
      let element = e.target
      const selected = element.classList.toggle('suggestion-clicked')
      if (selected) {
        // Add in map
        this.submitSurveyInfo.suggestions.push(element.innerText)
      } else {
        // remove from map
        this.submitSurveyInfo.suggestions =
          this.submitSurveyInfo.suggestions.filter(
            (suggestn) => suggestn !== element.innerText
          )
      }
      element.attributes['aria-pressed'].nodeValue = Boolean(selected)
      document
        .getElementById('selection-button-alert')
        .setAttribute(
          'aria-label',
          selected
            ? `${element.innerText} is selected`
            : `${element.innerText} is un-selected`
        )
      setTimeout(() => {
        document
          .getElementById('selection-button-alert')
          .setAttribute('aria-label', '')
      }, 0)
    },
    onAdditionalFeedbackAdd() {
      if (this.isReadOnly) return
      this.feedbackInputEnabled = true
      this.$nextTick(function () {
        this.$refs['additional-feedback-textarea']?.focus()
        this.$refs['additional-feedback-textarea']?.setAttribute(
          'aria-label',
          this.sendTranslatedValue(`${surveyForm.START_TYPING}`, 'Start Typing')
        )
      })
    },
    onRatingChangeHandler(sentinent) {
      if (this.isReadOnly) return
      this.submitSurveyInfo.feedbackValue =
        sentinent < this.schema?.surveyRatingTypeEnabledInfo?.criteria
          ? surveyForm.NEGATIVE
          : surveyForm.POSITIVE
      this.currSentinent = sentinent
      this.submitSurveyInfo.rating = sentinent
    },
    onYesResolutionButtonClicked(event) {
      if (this.isReadOnly) return
      this.$refs['yes-res-btn'].classList.toggle('suggestion-clicked')
      this.$refs['no-res-btn'].classList.remove('suggestion-clicked')
      this.submitSurveyInfo.issueResolved = true
      if (event) {
        const value =
          event.target.attributes['aria-pressed'].nodeValue === 'true'
        event.target.attributes['aria-pressed'].nodeValue = !value
      }
    },
    onNoResolutionButtonClicked(event) {
      if (this.isReadOnly) return
      this.$refs['no-res-btn'].classList.toggle('suggestion-clicked')
      this.$refs['yes-res-btn'].classList.remove('suggestion-clicked')
      this.submitSurveyInfo.issueResolved = false
      const value = event.target.attributes['aria-pressed'].nodeValue === 'true'
      event.target.attributes['aria-pressed'].nodeValue = !value
    },
    handleSurveySubmit() {
      if (!this.submitSurveyInfo.rating) return
      this.submitSurveyInfo.additionalFeedback = this.feebackText
      this.$emit('survey-submit', this.submitSurveyInfo)
      if (this.$store.state.isVoiceModeActive) {
        this.$store.state.shouldRestartVoiceRecording = true
      }
      focusOnChatInputField(true)
    },
    handleSurveySkip() {
      this.isReadOnly
        ? this.$emit('survey-closed')
        : this.$emit('survey-skipped')
      focusOnChatInputField(true)
      if (this.$store.state.isVoiceModeActive) {
        this.$store.state.shouldRestartVoiceRecording = true
      }
    },
    handlePersistentSurvey() {
      let childNodes = document.querySelector('.sentinel-box')?.childNodes
      childNodes = Array.from(childNodes).filter(
        ({ tagName }) => tagName?.toLowerCase() === 'button'
      )
      const rating =
        this.$store.getters.getCSATRating === feedback.POSITIVE
          ? 0
          : this.$store.getters.getCSATRating === feedback.NEGATIVE
            ? 1
            : 2
      childNodes[rating]?.click()
    }
  }
}
</script>
<style lang="scss" scoped>
@import '@/styles/css/theme.scss';
@import '@/styles/css/commonStyles.scss';
.form-box-wrapper {
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 2147483647;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  display: flex;
  overflow: auto;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
  .form-box {
    @include border-radius(var(--radius-chat-inner-component));
    background: #ffffff;
    padding: 24px;
    width: 100%;
    margin: auto;
    height: fit-content;
    max-width: 606px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    font-style: normal;
    font-weight: 600;
    line-height: 20px;
    &.form-box-compact {
      position: absolute;
      bottom: 0;
      padding: 24px;
    }
    &.form-box-compact-fullscreen {
      padding: 24px 0px 24px 24px;
      height: 100%;
    }

    .mandatory-text-container {
      display: flex;
      justify-content: end;
      margin-bottom: 16px;
      .mandatory-text {
        margin-right: 24px;
        font-weight: 400;
      }
    }

    .close-icon-container {
      display: flex;
      justify-content: end;
      &.close-icon-container-compact {
        padding-bottom: 20px;
        padding-right: 20px;
      }
      .close-icon-btn {
        cursor: pointer;
        background: none;
        border: none;
      }
    }
    .survey-header-txt {
      font-style: normal;
      font-weight: 500;
    }
    .rating-box {
      margin-top: 20px;
      &.rating-box-compact {
        display: flex;
        justify-content: space-between;
        align-items: start;
        :deep(.sentinel-box) {
          gap: 20px;
        }
      }
    }

    .sr-only {
      position: absolute !important;
      width: 1px !important;
      height: 1px !important;
      padding: 0 !important;
      margin: -1px !important;
      overflow: hidden !important;
      clip: rect(0, 0, 0, 0) !important;
      white-space: nowrap !important;
      border: 0 !important;
    }

    .survey-form-content {
      margin-top: 28px;
      .suggestion-container {
        display: flex;
        flex-direction: column;
        .suggestion-header-text {
          font-style: normal;
          font-weight: 600;
          line-height: 20px;
          margin-bottom: 16px;
        }
        .suggestion-options {
          display: flex;
          flex-flow: row wrap;
          -webkit-flex-flow: row wrap;
          gap: 8px;
          &.suggestion-options-compact {
            row-gap: 0px;
            column-gap: 8px;
          }
          .suggestions {
            margin-top: 8px;
          }
        }
      }

      .resolution {
        margin-bottom: 28px;
        .res-questn {
          font-style: normal;
          font-weight: 600;
          line-height: 20px;
          margin-bottom: 20px;
        }
        .resolution-buttons {
          display: flex;
          gap: 8px;
          .resolution-button {
            padding: 10px 64px !important;
          }
        }
      }

      .additional-feedback {
        display: flex;
        gap: 5px;
        margin-top: 28px;
        align-items: center;
        font-weight: 600;
        line-height: 20px;

        .additional-feedback-btn-container {
          cursor: pointer;

          :deep(svg) {
            path {
              fill: var(--widget-primary-color);
            }
          }
          .add-feedback-button {
            text-decoration: underline;
            cursor: pointer;
            color: var(--widget-primary-color);
          }
        }
      }

      .additional-feedback-input {
        margin-top: 28px;
        width: 100%;
        font-weight: 600;
        display: flex;
        flex-direction: column;
        .feedback-textarea {
          margin-top: 16px;
          margin-bottom: 8px;
          width: 100%;
          height: 84px;
          background: #ffffff;
          border: 1px solid #b0b0b0;
          border-radius: 4px;
          flex-direction: row;
          justify-content: flex-end;
          align-items: flex-end;
          padding: 12px 12px 12px 16px;
          &:focus {
            border: 1px solid var(--widget-primary-color);
          }
          &.feedback-textarea-disabled {
            cursor: not-allowed;
          }
        }
      }

      &.survey-form-content-compact {
        margin-top: 16px;
        margin-right: 24px;
        .resolution-compact {
          margin-bottom: 16px;
        }
        .additional-feedback-compact {
          margin-top: 26px;
        }
        .additional-feedback-input-compact {
          margin-top: 26px;
          max-width: 316px;
        }
      }
    }

    .form-box-content {
      overflow: auto;
      &.form-box-content-opened {
        height: 100%;
      }
      &.form-box-content-compact {
        overflow-y: hidden;
        overflow-x: hidden;
        box-sizing: border-box;
        scrollbar-width: thin;
        scrollbar-color: #6b6d7c #ffffff;
        ::-webkit-scrollbar-track {
          border: none;
        }
        &:hover {
          overflow-y: auto;
          scrollbar-gutter: stable;
        }
      }
    }
  }

  .buttons-outer-container {
    display: flex;
    justify-content: end;
    gap: 40px;
    border-top: 1px solid #e8e8e8;
    padding: 16px 32px 0px 32px;
    margin-right: -24px;
    margin-left: -24px;
    margin-top: 24px;
    .skip-button {
      cursor: pointer;
      color: var(--widget-primary-color);
      font-weight: 600;
    }
    .submit-button {
      cursor: pointer;
      background: var(--widget-primary-color);
      color: #fff;
      width: fit-content;
      padding: 8px 32px;
      font-style: normal;
      font-weight: 600;
      border-radius: 4px;
      &:disabled {
        cursor: not-allowed;
        background-color: #f3f5f6;
        color: #c3cbd0;
      }
    }

    &.buttons-outer-container-compact {
      border: none;
      justify-content: center;
      &.buttons-outer-container-compact-fullscreen {
        display: flex;
        flex-direction: column-reverse;
        justify-content: center;
        align-items: center;
        gap: 18px;
        padding: 16px 24px 0 0;
        margin: 0;
        .submit-button {
          width: 100%;
        }
      }
    }
  }
}
.custom-btn {
  border: 1px solid #b0b0b0;
  border-radius: 4px;
  padding: 10px 16px;
  cursor: pointer;
  background-color: #ffffff;
  color: #000;
  font-weight: 400;
  &.custom-btn-readOnly {
    cursor: not-allowed;
  }
}
.suggestion-clicked {
  border: 1px solid var(--widget-primary-color);
  background-color: var(--widget-primary-background-color);
}
.char-feedback-count {
  display: flex;
  justify-content: flex-end;
  color: #6d6d6d;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
}
.el-button {
  font-weight: 600;
}
.required-field {
  color: #e22c2c;
}
:deep(.el-textarea__inner) {
  border: 1px solid #dcdfe6 !important;
  margin-top: 8px;
}
</style>
